import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { Button, Ellipsis, Input, Skeleton, Tabs, Toast } from 'antd-mobile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import {
  IApiResult,
  ILeftMenu,
  ILoginUser,
  IOverlay,
  IPageContent,
  IPopup,
} from '../interfaces/app.interface';
import { loginStore } from '../stores/login.store';
import { selectedPageStore } from '../stores/selectedPage.store';
import { pageContentStore } from '../stores/page.store';
import { addPopupStore } from '../stores/popup.store';
import { PageLayout } from '../layouts/PageLayout';
import { PbFormBox } from '../components/PbFormBox';
import { PbFormBoxItem } from '../components/PbFormBoxItem';
import { MoreList } from '../components/MoreList';
import { SelectProject } from '../popups/SelectProject';
import { PerformanceRegistrationDetail } from '../popups/PerformanceRegistrationDetail';
import * as appUtil from '../utils/app.util';
import * as workApi from '../apis/work.api';
import { decode } from 'html-entities';
import moment from 'moment';
import { overlayStore } from '../stores/overlay.store';
// import SelectShop from '../popups/SelectShop/SelectShop.popup';

/**
 * 설치관리 > 실적등록
 * @constructor
 */

interface IPageProps {
  defaultSelectedPageName?: string;
}

const PerformanceRegistrationPage = ({
  defaultSelectedPageName = '',
}: PropsWithChildren<IPageProps>) => {
  // 언어를 정의함
  const { t } = useTranslation();

  // 로그인한 사용자 저장소를 정의함
  const [loginUser, setLoginUser] = useRecoilState<ILoginUser>(loginStore);

  // 선택한 페이지 저장소를 정의함
  const [selectedPage, setSelectedPage] =
    useRecoilState<ILeftMenu>(selectedPageStore);

  // 선택한 페이지의 이름을 정의함
  const [selectedPageName, setSelectedPageName] = useState<string>('');

  // 페이지 내용 저장소를 정의함
  const [pageContent, setPageContent] =
    useRecoilState<IPageContent>(pageContentStore);

  // 추가할 팝업 저장소를 정의함
  const [addPopup, setAddPopup] = useRecoilState<IPopup | null>(addPopupStore);

  // 화면 위 검은 화면 저장소를 정의함
  const [overlay, setOverlay] = useRecoilState<IOverlay>(overlayStore);

  // 목록 API 로딩 여부를 정의함
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 선택한 프로젝트를 정의함
  const [selectedProject, setSelectedProject] = useState<{
    label: string;
    value: string;
  }>({ label: '전체', value: '' });

  // Shop명 검색 폼을 정의함
  const [searchShop, setSearchShop] = useState<string>('');

  // 선택한 탭을 정의함
  // 2024.03.04 - 박재하 대리 요청으로 작업검수 화면일 때, '작업대기' 탭을 기본 선택으로 정의함
  const [selectedTab, setSelectedTab] = useState<string>(
    defaultSelectedPageName === '실적등록'
      ? 'PRCD'
      : defaultSelectedPageName === '작업검수'
      ? 'PRCD'
      : 'WORK_CMPL',
  );
  // const [selectedTab, setSelectedTab] = useState<string>(
  //   defaultSelectedPageName === '실적등록' ? 'PRCD' : 'WORK_CMPL',
  // );

  // 목록 데이터를 정의함
  const [listData, setListData] = useState<any[]>([]);

  // 선택한 목록 페이지를 정의함
  const currentPageRef = useRef<number>(1);

  // 탭을 정의함
  const tabItem: any[] = useMemo(() => {
    return [
      { key: '', title: '전체' },
      {
        key: 'PRCD',
        title: '작업대기',
      },
      {
        key: 'WORK_CMPL',
        title: '작업완료',
      },
      {
        key: 'NSPC_CMPL',
        title: '검수완료',
      },
      {
        key: 'PM_NSPC_CMPL',
        title: 'PM검수완료',
      },
    ];
  }, []);

  // 작업 상태를 정의함
  const workState: any[] = useMemo(() => {
    return [
      {
        value: 'PRCD',
        iconTitle: (
          <>
            작업
            <br />
            대기
          </>
        ),
        leading: 'leading-tight',
        backgroundColor: 'bg-orange-500',
      },
      {
        value: 'WORK_CMPL',
        iconTitle: (
          <>
            작업
            <br />
            완료
          </>
        ),
        leading: 'leading-tight',
        backgroundColor: 'bg-lime-600',
      },
      {
        value: 'NSPC_CMPL',
        iconTitle: (
          <>
            검수
            <br />
            완료
          </>
        ),
        leading: 'leading-tight',
        backgroundColor: 'bg-indigo-500',
      },
      {
        value: 'PM_NSPC_CMPL',
        iconTitle: (
          <>
            PM
            <br />
            검수
            <br />
            완료
          </>
        ),
        leading: 'leading-none',
        backgroundColor: 'bg-gray-600',
      },
    ];
  }, []);

  // 목록을 불러옴
  const getListData = () => {
    // // 화면 위 검은 화면 저장소에 적용함
    // setOverlay({
    //   visible: true,
    //   useLightMode: true,
    //   content: (
    //     <div className="px-3 py-1 bg-black/50">
    //       <span className="text-base text-white font-bold">불러오는 중</span>
    //     </div>
    //   ),
    // });

    setIsLoading(true);

    workApi
      .getWorkSttMbl({
        cmplFlag: '',
        nstlSttsCode: selectedTab,
        prjcCode: selectedProject.value,
        splrTypeCode: '',
        shipToCode: searchShop,
        searchType: 'cntr_name',
        searchKeywork: '',
        currPageIdx: currentPageRef.current,
        pageSize: 30,
      })
      .then((data: IApiResult) => {
        if (data.data.list !== undefined) {
          // 더 이상 불러올 목록 데이터가 없으면 종료함
          if (currentPageRef.current > 1 && data.data.list.length === 0) {
            Toast.show({
              position: 'bottom',
              content: '마지막 페이지입니다.',
            });

            return;
          }

          // 목록 데이터에 적용함
          if (currentPageRef.current === 1) {
            // 첫 페이지면 목록 데이터를 그대로 적용함
            setListData(data.data.list);
          } else {
            // 첫 페이지가 아니면 목록 데이터를 추가함
            setListData((pre: any) => [...pre, ...data.data.list]);
          }
        }
      })
      .catch((error: any) => {})
      .finally(() => {
        // // 화면 위 검은 화면 저장소에 적용함
        // setOverlay({
        //   visible: false,
        //   content: '',
        // });
        setIsLoading(false);
      });
  };

  // 더보기 버튼을 클릭함
  const handleMoreListButton_onClick = () => {
    // 현재 페이지를 증가함
    currentPageRef.current += 1;

    // 목록을 불러옴
    getListData();
  };

  // 프로젝트 선택 버튼을 클릭함
  const handleProjectButton_onClick = () => {
    let tmpId: string = nanoid();

    // 팝업을 추가함
    setAddPopup({
      id: tmpId,
      title: '프로젝트 선택',
      content: (
        <SelectProject
          id={tmpId}
          callback={(event: any) => {
            // 선택한 프로젝트를 정의함
            setSelectedProject({
              label: event.prjcName,
              value: event.prjcCode,
            });
          }}
        />
      ),
      widthSizePercent: 100,
      heightSizePercent: 90,
      position: 'bottom',
      maskClick: true,
      applyPadding: false,
    });
  };

  // Shop명 입력칸에서 엔터키를 누름
  const handleSearchShopInput_onKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      // 검색 버튼을 클릭함
      handleSearchButton_onClick();
    }
  };

  // 검색 버튼을 클릭함
  const handleSearchButton_onClick = () => {
    // 현재 페이지를 초기화함
    currentPageRef.current = 1;

    // 목록을 불러옴
    getListData();
  };

  // 목록의 행을 클릭함
  const handleList_onClick = (event: any) => {
    let tmpId: string = nanoid();

    // 팝업을 추가함
    setAddPopup({
      id: tmpId,
      title: event.shipToCode || 'Shop 미지정',
      content: (
        <PerformanceRegistrationDetail
          id={tmpId}
          selectedData={event}
          callback={(result: any) => {
            if (result === 'refresh-list') {
              currentPageRef.current = 1;

              // 목록을 불러옴
              getListData();
            }
          }}
        />
      ),
      widthSizePercent: 100,
      heightSizePercent: 90,
      position: 'bottom',
      maskClick: true,
      applyPadding: false,
    });
  };

  // 페이지 로딩 후 한번만 실행함
  useEffect(() => {
    // 스크롤 버튼의 세로 위치를 이동함
    appUtil.moveVerticalPositionScrollButton('page', '', false);

    return () => {};
  }, []);

  // 페이지의 제목이 변경될 때 실행함
  useEffect(() => {
    if (selectedPageName === '') {
      return;
    }

    // // 선택한 탭에 적용함
    // setSelectedTab(selectedPageName === '실적등록' ? 'PRCD' : 'WORK_CMPL');

    return () => {};
  }, [selectedPageName]);

  // 선택한 페이지가 생성됐을 때 실행함
  useEffect(() => {
    if (Object.keys(selectedPage).length === 0) {
      return;
    }

    let tmpSelectedPageName: string = '';

    // 내비게이션에 1딘계 페이지를 추가함
    if (selectedPage.oneMenuId) {
      tmpSelectedPageName = selectedPage.oneKrnMenu;
    }

    // 내비게이션에 2딘계 페이지를 추가함
    if (selectedPage.twoMenuId) {
      tmpSelectedPageName = selectedPage.twoKrnMenu;
    }

    // 내비게이션에 3딘계 페이지를 추가함
    if (selectedPage.threeMenuId) {
      tmpSelectedPageName = selectedPage.threeKrnMenu;
    }

    // 선택한 페이지의 이름에 적용함
    setSelectedPageName(tmpSelectedPageName);

    return () => {};
  }, [selectedPage]);

  // 검색 항목이 변경될 때 실행함
  useEffect(() => {
    if (loginUser.id === '') {
      return;
    }

    // 현재 페이지를 초기화함
    currentPageRef.current = 1;

    // 목록을 불러옴
    getListData();

    return () => {};
  }, [loginUser.id, selectedProject.value]);

  // 목록 데이터, 탭이 변경될 때 실행함
  useEffect(() => {
    // 현재 페이지를 초기화함
    currentPageRef.current = 1;

    // 목록을 불러옴
    getListData();

    return () => {};
  }, [selectedTab]);

  return (
    <PageLayout
      pageInfoBarLeftArea={selectedPageName}
      pageInfoBarCenterArea={<></>}
      pageInfoBarRightArea={<></>}
    >
      <PbFormBox label="검색">
        <div className="space-y-3">
          {/* 항목 */}
          <PbFormBoxItem label="프로젝트">
            {/* 버튼 */}
            <Button
              size="middle"
              fill="outline"
              onClick={handleProjectButton_onClick}
              className="w-full !h-11 !border-rose-500"
            >
              <span className="text-md text-gray-700 font-semibold">
                <Ellipsis direction="end" content={selectedProject.label} />
              </span>
            </Button>
          </PbFormBoxItem>

          {/* 항목 */}
          <PbFormBoxItem label="Shop명">
            <div className="flex justify-center items-center space-x-1">
              <Input
                // placeholder="Shop명을 입력하세요."
                clearable={true}
                value={searchShop}
                onChange={(value: any) => {
                  setSearchShop(value);
                }}
                onKeyDown={handleSearchShopInput_onKeyDown}
                className="ant-m-input w-2/3"
              />

              <Button
                shape="default"
                onClick={handleSearchButton_onClick}
                className="w-1/3 !bg-blue-500"
              >
                <span className="text-md text-white">검색</span>
              </Button>
            </div>
          </PbFormBoxItem>
        </div>
      </PbFormBox>

      {/* 탭 */}
      <Tabs
        onChange={setSelectedTab}
        activeKey={selectedTab}
        className="ant-m-tabs ant-m-tabs-page-sticky"
      >
        {tabItem.map((tabItem: any) => (
          <Tabs.Tab key={tabItem.key} title={tabItem.title}>
            <div className="space-y-1">
              {/* 로딩 중 */}
              {isLoading && (
                <div>
                  <Skeleton.Title animated />
                  <Skeleton.Paragraph lineCount={5} animated />
                </div>
              )}

              {!isLoading &&
                listData.map((listItem: any, listIndex: number) => (
                  <div
                    key={listIndex}
                    onClick={() => handleList_onClick(listItem)}
                    className="button-bg-event py-2 flex space-x-3"
                  >
                    {/* 번호 */}
                    <div className="flex-none w-7 flex justify-center items-center">
                      <span className="text-sm text-gray-700">
                        {listItem.no}
                      </span>
                    </div>

                    {/* 작업상태 */}
                    <div className="flex-none flex justify-center items-center">
                      <div
                        className={[
                          'flex-none w-12 h-12 flex justify-center items-center rounded-full overflow-hidden',
                          _.find(workState, { value: listItem.nstlSttsCode })
                            ?.backgroundColor,
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'text-xs text-white font-bold',
                            _.find(workState, { value: listItem.nstlSttsCode })
                              ?.leading,
                          ].join(' ')}
                        >
                          {
                            _.find(workState, { value: listItem.nstlSttsCode })
                              ?.iconTitle
                          }
                        </span>
                      </div>
                    </div>

                    <div className="grow flex justify-start items-center overflow-hidden">
                      <div className="w-full space-y-0.5">
                        {/* 첫번째 행 */}
                        <div className="flex justify-start items-center space-x-2">
                          {/* ESL 설치일자 */}
                          {listItem.splrTypeCode === 'ESL_NSTL' && (
                            <span className="text-xs text-gray-700 font-normal">
                              {moment(listItem.xpctDateEsl).format(
                                'YYYY.MM.DD',
                              ) || '설치일자 없음'}
                            </span>
                          )}
                          {/* 네트워크 설치일자 */}
                          {listItem.splrTypeCode === 'NTWR_CNST' && (
                            <span className="text-xs text-gray-700 font-normal">
                              {moment(listItem.xpctDateNtwr).format(
                                'YYYY.MM.DD',
                              ) || '설치일자 없음'}
                            </span>
                          )}
                          {/* 납품일자 */}
                          {(listItem.splrTypeCode === 'ESL_DLVR' ||
                            listItem.splrTypeCode === '' ||
                            listItem.splrTypeCode === null) && (
                            <span className="text-xs text-gray-700 font-normal">
                              {moment(listItem.xpctDate).format('YYYY.MM.DD') ||
                                '납품일자 없음'}
                            </span>
                          )}

                          <div className="flex justify-center items-center">
                            <FontAwesomeIcon
                              icon={['fas', 'circle']}
                              className="w-0.5 h-0.5 text-gray-300"
                            />
                          </div>

                          {/* Shop */}
                          <span className="text-xs text-gray-700 font-semibold">
                            <Ellipsis
                              direction="end"
                              content={decode(
                                listItem.shipToCode || 'Shop 미지정',
                              )}
                            />
                          </span>
                        </div>

                        {/* 두번째 행 */}
                        <div className="flex justify-start items-center space-x-2">
                          {/* 작업구분, 작업장소 */}
                          <div className="flex justify-center items-center space-x-1">
                            {/* 작업구분 */}
                            {listItem.splrTypeCode === 'ESL_NSTL' && (
                              <div className="px-1 h-5 flex justify-center items-center bg-sky-100 border border-sky-400 rounded leading-none">
                                <span className="text-xs text-gray-500 font-bold">
                                  ESL
                                </span>
                              </div>
                            )}
                            {listItem.splrTypeCode === 'NTWR_CNST' && (
                              <div className="px-1 h-5 flex justify-center items-center bg-sky-500 border border-sky-600 rounded leading-none">
                                <span className="text-xs text-white font-bold">
                                  NET
                                </span>
                              </div>
                            )}
                            {(listItem.splrTypeCode === 'ESL_DLVR' ||
                              listItem.splrTypeCode === '' ||
                              listItem.splrTypeCode === null) && (
                              <>
                                <div className="px-1 h-5 flex justify-center items-center bg-green-600 border border-green-700 rounded leading-none">
                                  <span className="text-xs text-white font-bold">
                                    납품
                                  </span>
                                </div>

                                {/* 상태 */}
                                {/*<div className="px-1 h-5 flex justify-center items-center bg-white border border-gray-400 rounded leading-none overflow-hidden">*/}
                                {/*  <span className="text-xs text-gray-500 font-bold">*/}
                                {/*    {listItem.nstlSttsName}*/}
                                {/*  </span>*/}
                                {/*</div>*/}
                              </>
                            )}
                            {/* 팀이름 */}
                            {listItem.teamName && (
                              <div className="px-1 h-5 flex justify-center items-center bg-white border border-gray-400 rounded leading-none overflow-hidden">
                                <span className="text-xs text-gray-500 font-bold">
                                  {decode(listItem.teamName)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* 세번째 행 */}
                        <div className="flex justify-start items-center truncate">
                          {/* 계약명 */}
                          <span className="text-xs text-gray-900 font-medium truncate">
                            {decode(listItem.ttl || '')}
                          </span>
                        </div>

                        {/* 가로 분리선 */}
                        <div className="w-full h-px bg-gray-200 border-b"></div>

                        {/* 네번째 행 */}
                        <div className="flex justify-start items-center space-x-2">
                          {/* 계약명 */}
                          <span className="text-base text-gray-900 font-medium">
                            <Ellipsis
                              direction="end"
                              content={decode(listItem.cntrName || '')}
                            />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-none w-5 flex justify-center items-center">
                      {/* 아이콘 */}
                      <div className="flex justify-center items-center">
                        <FontAwesomeIcon
                          icon={['fas', 'chevron-right']}
                          className="w-3 h-3 text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}

              {/* 더 불러오기 */}
              {!isLoading && (
                <MoreList onClick={handleMoreListButton_onClick} />
              )}
            </div>
          </Tabs.Tab>
        ))}
      </Tabs>
    </PageLayout>
  );
};

export default PerformanceRegistrationPage;
